import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Sparkles } from "lucide-react";
import { HEROES, HERO_ORDER } from "../data/heroes";
import HeroNarrateButton from "./HeroNarrateButton";

/**
 * HeroCompanion — A Brain Superhero presence woven into every page.
 *
 * variants:
 *  - "banner"  → wide horizontal hero greeting card (Dashboard top, Journal top)
 *  - "watch"   → small avatar bubble that "watches over" a form (MediaJournal)
 *  - "footer"  → inline companion strip (Activities top)
 *  - "badge"   → tiny avatar chip (used on activity cards)
 *
 * heroKey: optionally pin a specific hero; otherwise rotates daily.
 */
const HeroCompanion = ({ variant = "banner", heroKey = null, customMessage = null, onClick = null }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || "en").slice(0, 2);

  // Daily-rotating hero (deterministic by day-of-year)
  const dailyHero = HERO_ORDER[new Date().getDate() % HERO_ORDER.length];
  const activeKey = heroKey || dailyHero;
  const hero = HEROES[activeKey];
  const tr = t(`heroes.${activeKey}`, { returnObjects: true });

  const defaultMessages = {
    queen: { en: "You are enough. You matter. You choose you.", es: "Eres suficiente. Importas. Te eliges a ti." },
    victor: { en: "Your voice matters. Speak up. I've got your back.", es: "Tu voz importa. Habla. Estoy contigo." },
    cam: { en: "Take a slow breath. The Power of Calm is yours.", es: "Respira lento. El Poder de la Calma es tuyo." },
  };
  const msg = customMessage || defaultMessages[activeKey][lang];

  const handleClick = () => {
    if (onClick) return onClick(activeKey);
    navigate(`/activities?hero=${activeKey}`);
  };

  if (variant === "badge") {
    return (
      <button
        onClick={handleClick}
        data-testid={`hero-companion-badge-${activeKey}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ${hero.chip} text-xs font-semibold hover:scale-105 transition-transform`}
        title={tr.name}
      >
        <div className={`w-5 h-5 rounded-full ${hero.imageBg} overflow-hidden flex items-end justify-center flex-shrink-0`}>
          <img src={hero.image} alt={tr.name} className="max-w-full max-h-full object-contain" />
        </div>
        <span>{tr.pathTitle}</span>
      </button>
    );
  }

  if (variant === "watch") {
    return (
      <div
        className={`relative rounded-2xl ${hero.imageBg} p-3 flex items-end justify-center w-20 h-24 shadow-lg`}
        data-testid={`hero-companion-watch-${activeKey}`}
        title={tr.name}
      >
        <img src={hero.image} alt={tr.name} className="max-w-full max-h-full object-contain" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
      </div>
    );
  }

  if (variant === "footer") {
    return (
      <div
        onClick={handleClick}
        data-testid={`hero-companion-footer-${activeKey}`}
        className={`flex items-center gap-3 bg-gradient-to-r ${hero.soft} rounded-xl px-4 py-2 cursor-pointer hover:shadow-md transition-all border-l-4`}
        style={{ borderLeftColor: hero.accent }}
      >
        <div className={`${hero.imageBg} rounded-lg p-1 w-10 h-12 flex items-end justify-center flex-shrink-0`}>
          <img src={hero.image} alt={tr.name} className="max-w-full max-h-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-gray-500">{lang === "es" ? "Tu héroe del día" : "Your Hero Today"}</div>
          <p className="text-sm font-semibold text-gray-900 truncate">{tr.name}</p>
        </div>
        <HeroNarrateButton hero={activeKey} text={msg} label="" />
      </div>
    );
  }

  // banner (default)
  return (
    <Card
      data-testid={`hero-companion-banner-${activeKey}`}
      className={`overflow-hidden border-0 bg-gradient-to-r ${hero.soft} ${hero.bgGlow} cursor-pointer`}
      onClick={handleClick}
    >
      <div className={`h-1.5 bg-gradient-to-r ${hero.gradient}`} />
      <div className="grid grid-cols-[100px_1fr] sm:grid-cols-[140px_1fr] gap-3 items-center">
        <div className={`${hero.imageBg} flex items-end justify-center p-2 h-full`}>
          <img src={hero.image} alt={tr.name} className="max-w-full max-h-32 object-contain drop-shadow-lg" />
        </div>
        <div className="p-4 pr-5">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${hero.chip} border-0 text-xs`}>
              <Sparkles className="w-3 h-3 mr-1" />
              {lang === "es" ? "Héroe del día" : "Hero of the Day"}
            </Badge>
          </div>
          <h3 className="font-extrabold text-gray-900 text-lg leading-tight">{tr.name}</h3>
          <p className="text-sm text-gray-700 italic mt-1 leading-snug">"{msg}"</p>
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <HeroNarrateButton hero={activeKey} text={msg} label={lang === "es" ? "Escuchar" : "Listen"} />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default HeroCompanion;
